import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PageTransition from '../components/Motion/PageTransition';

/**
 * Consulting Services Page - Data Strategy Consulting offerings
 * Focused on accurate, real consulting services provided by Real Value Group
 */
function ConsultingServices() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const consultingServices = [
    {
      id: 'data-driven-strategy',
      name: 'Data-Driven Strategy Development',
      description: "We analyze your business data to uncover what's working, what's not, and where the biggest opportunities lie. Our advisory helps you align your strategy with evidence-based insights rather than assumptions.",
      whatWeDeliver: [
        'Performance metrics analysis and interpretation',
        'Customer behavior pattern identification',
        'Operational efficiency assessments',
        'Revenue and profitability trend analysis',
        'Competitive positioning insights',
        'Strategic recommendations based on your data'
      ],
      benefits: 'Make strategic decisions backed by your actual business data, not guesswork. Understand the real drivers of your success.',
      realWorldExample: 'Financial performance analysis leading to cost optimization strategies'
    },
    {
      id: 'opportunity-identification',
      name: 'Opportunity & Gap Analysis',
      description: 'Through deep analysis of your operations, we identify untapped opportunities and areas holding you back. We help you prioritize initiatives that will create the greatest impact on your business.',
      whatWeDeliver: [
        'Market opportunity assessment from your data',
        'Process inefficiency identification',
        'Resource allocation analysis',
        'Growth bottleneck discovery',
        'Quick-win opportunity mapping',
        'ROI-based prioritization framework'
      ],
      benefits: 'Focus your efforts where they matter most. Discover hidden opportunities in your existing data.',
      realWorldExample: 'Warehouse efficiency analysis revealing optimization opportunities worth 20% productivity gain'
    },
    {
      id: 'performance-improvement',
      name: 'Performance Improvement Guidance',
      description: 'We guide you on the specific actions to take for improving operations, financials, and customer outcomes. Our recommendations are grounded in what your data reveals about your business.',
      whatWeDeliver: [
        'KPI definition and target setting',
        'Performance benchmark establishment',
        'Improvement action plans',
        'Success metrics identification',
        'Progress tracking frameworks',
        'Continuous improvement roadmaps'
      ],
      benefits: 'Get clear direction on what to improve and how. Track progress with meaningful metrics.',
      realWorldExample: 'Exact Online P&L optimization strategies based on transaction-level analysis'
    },
    {
      id: 'decision-support',
      name: 'Decision Support & Scenario Planning',
      description: 'We help you evaluate different business scenarios using your data, enabling confident decision-making for investments, expansions, cost reductions, or strategic pivots.',
      whatWeDeliver: [
        'What-if scenario modeling',
        'Impact analysis for proposed changes',
        'Risk assessment based on historical data',
        'Cost-benefit analysis frameworks',
        'Decision criteria development',
        'Data-backed recommendation reports'
      ],
      benefits: 'Make major decisions with confidence. Understand potential outcomes before committing resources.',
      realWorldExample: 'Multi-scenario financial modeling for business expansion decisions'
    },
    {
      id: 'business-health-reviews',
      name: 'Business Health Check Reviews',
      description: 'Regular reviews of your business performance data to spot trends, identify concerns early, and ensure you stay on track toward your goals.',
      whatWeDeliver: [
        'Monthly or quarterly performance reviews',
        'Trend analysis and forecasting',
        'Early warning indicator monitoring',
        'Health scorecard development',
        'Executive summary reporting',
        'Action item recommendations'
      ],
      benefits: 'Stay ahead of problems. Get regular insights into your business health and proactive recommendations.',
      realWorldExample: 'Ongoing financial health monitoring for multi-division organizations'
    },
    {
      id: 'data-literacy-coaching',
      name: 'Data Literacy & Analytics Coaching',
      description: 'We help your leadership team become more data-literate, teaching them how to ask better questions, interpret insights correctly, and make evidence-based decisions confidently.',
      whatWeDeliver: [
        'Executive coaching on data interpretation',
        'How to ask analytical questions workshops',
        'Dashboard insight reading sessions',
        'Data storytelling guidance',
        'Critical thinking development',
        'Best practice sharing'
      ],
      benefits: 'Build internal capability. Empower your team to leverage data independently.',
      realWorldExample: 'Leadership training on interpreting Power BI dashboards and acting on insights'
    }
  ];

  return (
    <PageTransition>
      <div className="bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="container mx-auto px-4">
          {/* Header Section */}
          <motion.div
            className="max-w-4xl mx-auto text-center mb-16"
            initial="hidden"
            animate="visible"
            variants={headerVariants}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Data Advisory Services
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-6">
              We provide expert advisory and consultation grounded in the reality of your business data.
            </p>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              By analyzing your operations, performance metrics, and customer behavior, we help you understand what's truly driving results—and what's holding you back. Our team guides you on the right actions to take, the opportunities to pursue, and the improvements that will create the greatest impact.
            </p>
            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-600 mb-8 max-w-3xl mx-auto">
              <p className="text-lg text-gray-800 font-medium">
                <strong>Impact:</strong> Turn complex data into clear direction. Make decisions with confidence. Strengthen your strategy with insights that are tailored, precise, and backed by evidence. Our advisory services ensure you take the right steps for growth, efficiency, and long-term success—based on what your data is telling you.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-blue-50 rounded-full text-blue-700 font-semibold">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
              </svg>
              Practical, Results-Driven Advisory
            </div>
          </motion.div>

          {/* Services Grid */}
          <motion.div
            className="max-w-7xl mx-auto space-y-8"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            {consultingServices.map((service) => (
              <motion.div
                key={service.id}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
                variants={cardVariants}
                whileHover={{ y: -4 }}
              >
                <div className="p-8">
                  {/* Service Name */}
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">{service.name}</h2>

                  {/* Description */}
                  <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                    {service.description}
                  </p>

                  {/* What We Deliver */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                      What We Deliver
                    </h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      {service.whatWeDeliver.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm text-gray-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Benefits */}
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-600 mb-4">
                    <p className="text-sm font-semibold text-gray-800">
                      <span className="text-blue-700">Key Benefits:</span> {service.benefits}
                    </p>
                  </div>

                  {/* Real World Example */}
                  <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                    <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <span className="font-semibold">Real-World Application:</span>
                      <span className="ml-1">{service.realWorldExample}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Section */}
          <motion.div
            className="max-w-4xl mx-auto text-center mt-16 p-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl text-white shadow-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-4">Ready to Make Data-Driven Decisions?</h2>
            <p className="text-blue-100 mb-6 text-lg">
              Let's discuss how our advisory services can help you understand and act on your business data
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-lg"
              >
                Schedule a Consultation
              </Link>
              <Link
                to="/projects"
                className="inline-block bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors border-2 border-white"
              >
                View Our Work
              </Link>
            </div>
          </motion.div>

          {/* Back to Services */}
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-semibold transition-colors group"
            >
              <svg
                className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Services
            </Link>
          </div>
        </div>
      </div>
    </PageTransition>
  );
}

export default ConsultingServices;

